import React, { useId, useRef, useState } from 'react';
import { DeviceId, devices, plates } from '../data/siteContent';

/**
 * Catalog wall. Every plate is a real capture from the shipping app on the
 * selected device — the same set used for the store listing, minus the
 * dictionaries shot, which was captured mid-transition and needs a retake.
 */
const ScreenshotsSection: React.FC = () => {
  const [device, setDevice] = useState<DeviceId>('iphone');
  const tabsId = useId();
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const active = devices.find((item) => item.id === device) ?? devices[0];

  // Arrow keys move between tabs, as expected of a tablist.
  const onKeyDown = (event: React.KeyboardEvent) => {
    const offset = event.key === 'ArrowRight' ? 1 : event.key === 'ArrowLeft' ? -1 : 0;
    if (offset === 0) return;
    event.preventDefault();
    const index = devices.findIndex((item) => item.id === device);
    const next = devices[(index + offset + devices.length) % devices.length];
    setDevice(next.id);
    tabRefs.current[next.id]?.focus();
  };

  return (
    <section id="screens" className="border-y border-ink/12 bg-surface section-y">
      <div className="shell">
        <div className="flex flex-wrap items-end justify-between gap-8">
          <div className="max-w-xl">
            <p className="kicker-action">Plates</p>
            <h2 className="display-lg mt-5 text-[2.25rem] sm:text-[3rem]">
              Five screens, no mock-ups.
            </h2>
            <p className="prose-note mt-6">
              Captured on device, in the sepia theme, reading an Italian novel from Project Gutenberg.
            </p>
          </div>

          <div
            role="tablist"
            aria-label="Choose a device"
            onKeyDown={onKeyDown}
            className="flex border border-ink/20"
          >
            {devices.map((item) => {
              const selected = item.id === device;
              return (
                <button
                  key={item.id}
                  ref={(node) => {
                    tabRefs.current[item.id] = node;
                  }}
                  type="button"
                  role="tab"
                  id={`${tabsId}-tab-${item.id}`}
                  aria-selected={selected}
                  aria-controls={`${tabsId}-panel`}
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setDevice(item.id)}
                  className={`border-r border-ink/20 px-4 py-2.5 font-body text-[0.6875rem] font-semibold uppercase tracking-small-caps transition last:border-r-0 ${
                    selected ? 'bg-primary text-white' : 'bg-background text-muted hover:text-ink'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>

        <div
          id={`${tabsId}-panel`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-tab-${device}`}
          className="mt-14"
        >
          <ul className="grid list-none grid-cols-1 gap-x-gutter gap-y-14 sm:grid-cols-2 lg:grid-cols-3">
            {plates.map((plate) => (
              <li key={plate.id}>
                <figure>
                  <div className="plate relative bg-background p-2 transition-shadow duration-200 hover:shadow-card-hover">
                    <span
                      className="absolute -left-px -top-px z-10 bg-primary px-2 py-1 font-display text-xs text-white"
                      aria-hidden="true"
                    >
                      {plate.index}
                    </span>
                    {/* Framed to a fixed ratio rather than shown whole: a full
                        phone capture at this width runs past 600px tall and the
                        wall stops reading as a wall. */}
                    <img
                      key={`${device}-${plate.id}`}
                      src={`/screenshots/${device}/${plate.id}.png`}
                      width={active.width}
                      height={active.height}
                      alt={`${plate.alt} — on ${active.note}`}
                      className={`block aspect-[4/5] w-full object-cover ${
                        plate.focus === 'bottom' ? 'object-bottom' : 'object-top'
                      }`}
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  <figcaption className="mt-4 border-t border-ink/15 pt-3">
                    <h3 className="display-sm text-lg">{plate.title}</h3>
                    <p className="prose-note mt-1.5 text-[0.875rem]">{plate.description}</p>
                  </figcaption>
                </figure>
              </li>
            ))}

            {/* Five plates in a three-column grid leave one cell empty. Fill it
                with the capture metadata rather than a gap. */}
            <li className="hidden lg:block">
              <dl className="border border-ink/15 bg-background p-6">
                <p className="kicker-action">Capture notes</p>
                <div className="mt-5 border-t border-ink/12 pt-3">
                  <dt className="kicker">Device</dt>
                  <dd className="prose-note mt-1 text-[0.875rem]">{active.note}</dd>
                </div>
                <div className="mt-3 border-t border-ink/12 pt-3">
                  <dt className="kicker">Theme</dt>
                  <dd className="prose-note mt-1 text-[0.875rem]">Sepia, the app default</dd>
                </div>
                <div className="mt-3 border-t border-ink/12 pt-3">
                  <dt className="kicker">Book</dt>
                  <dd className="prose-note mt-1 text-[0.875rem]">
                    <em>La favorita del Mahdi</em>, Emilio Salgari — Project Gutenberg #25180
                  </dd>
                </div>
                <div className="mt-3 border-t border-ink/12 pt-3">
                  <dt className="kicker">Framing</dt>
                  <dd className="prose-note mt-1 text-[0.875rem]">
                    Each plate is cropped from a full-screen capture. Nothing is retouched.
                  </dd>
                </div>
              </dl>
            </li>
          </ul>

          <p className="kicker mt-12 lg:hidden">{active.note}</p>
        </div>
      </div>
    </section>
  );
};

export default ScreenshotsSection;
